import { Permission, alphabeticalSort, notEqual } from 'shared'
import { toLabeledValues } from 'components/CIMSSelect/utils/labeledValues'
import { EditRowActionOptions } from 'components/CIMSTable/CIMSTableContext/types/actions/row'
import {
  PayItemConnection,
  useConnectionsContext,
} from '../../ConnectionsContext'
import EditBody from './EditBody'
import { EditBodyValues } from './EditBody/types'
import { useEditGoogleSheetPayItemConnection } from '../../../../../../hooks/useEditGoogleSheetPayItemConnection'

export function useEdit(): EditRowActionOptions<
  PayItemConnection,
  EditBodyValues
> {
  const { refetch } = useConnectionsContext()
  const edit = useEditGoogleSheetPayItemConnection()
  return {
    permission: Permission.EditPayItemConnection,
    modal: {
      type: 'simple',
      height: 'tall',
      body: ({ googleSheet }) => (
        <EditBody
          headers={
            googleSheet
              ? googleSheet.headers.map(
                  ({ id, formattedValue: label, order, visible }) => ({
                    label,
                    id,
                    order,
                    visible,
                  })
                )
              : []
          }
          googleSheet={!!googleSheet}
        />
      ),
      recordName: ({ name, googleSheet }) =>
        name ?? googleSheet?.title ?? 'Connection',
      initialValues: ({ name, googleSheet, group }) => ({
        name,
        group: group
          ? toLabeledValues([group], {
              type: 'options',
              label: 'name',
              value: 'id',
              tooltip: 'description',
            })[0]
          : undefined,
        googleSheet: googleSheet
          ? {
              documentId: googleSheet.documentId,
              payItemColumnKey: googleSheet.payItemColumnKey ?? 'Pay Items',
              sheetId: googleSheet.sheetId.toString(),
            }
          : undefined,
      }),
      onOk: async (connection, values) => {
        const { name, googleSheet } = values
        const groupId = values.group?.value as string
        const headers = (values.headers ?? []).map(
          ({ id, visible }, order) => ({ id, visible, order })
        )
        const nameDifferent = connection.name !== name
        const groupDifferent = connection.group?.id !== groupId
        let promise
        if (googleSheet) {
          const { googleSheet: sheet } = connection
          const { documentId, payItemColumnKey } = googleSheet
          const sheetId = parseInt(googleSheet.sheetId)
          if (
            nameDifferent ||
            groupDifferent ||
            sheet?.documentId !== documentId ||
            sheet?.sheetId !== sheetId ||
            sheet?.payItemColumnKey !== payItemColumnKey ||
            notEqual(
              headers,
              sheet?.headers?.map(({ id, order, visible }) => ({
                id,
                order,
                visible,
              })),
              { compareFn: alphabeticalSort('id') }
            )
          )
            promise = edit({
              variables: {
                input: {
                  connectionId: connection.id,
                  documentId,
                  sheetId,
                  groupId: groupId ?? null,
                  name,
                  payItemColumnKey,
                  headers,
                },
              },
            })
        }
        if (promise) {
          await promise
          refetch()
        }
      },
    },
  }
}
